import { useEffect, useState } from 'react'
import { supabase } from '../../services/supabaseClient'
import { useAppSelector } from '../../store/hooks'
import { SECTIONS } from '../../data/sections'
import type { SectionId } from '../../types/tools'
import styles from './MyTeamPanel.module.css'

interface MemberProgress {
  name: string
  count: number
  isMe: boolean
}

// Count all tools across both sections
function getTotalTools(): number {
  let total = 0
  for (const sk of Object.keys(SECTIONS) as SectionId[]) {
    for (const ck of Object.keys(SECTIONS[sk].cats)) {
      const cat = (SECTIONS[sk].cats as unknown as Record<string, { tools: Record<string, unknown> }>)[ck]
      total += Object.keys(cat.tools).length
    }
  }
  return total
}

export default function MyTeamPanel() {
  const profile = useAppSelector((s) => s.auth.profile)
  const userId = useAppSelector((s) => s.auth.user?.id)
  const [teamPct, setTeamPct] = useState(0)
  const [teamCompleted, setTeamCompleted] = useState(0)
  const [teamTotal, setTeamTotal] = useState(0)
  const [members, setMembers] = useState<MemberProgress[]>([])
  const [loading, setLoading] = useState(true)

  const totalTools = getTotalTools()

  useEffect(() => {
    if (!profile?.team_id || !userId) {
      setLoading(false)
      return
    }

    async function loadTeamProgress() {
      setLoading(true)

      // Get team profiles
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, display_name, email')
        .eq('team_id', profile!.team_id!)

      if (!profiles || profiles.length === 0) {
        setLoading(false)
        return
      }

      // Get all responses to count completions
      const { data: responses } = await supabase
        .from('responses')
        .select('user_id, tool_id')

      // Build response map: userId -> set of toolIds
      const responseMap = new Map<string, Set<string>>()
      responses?.forEach((r) => {
        if (!responseMap.has(r.user_id)) responseMap.set(r.user_id, new Set())
        responseMap.get(r.user_id)!.add(r.tool_id)
      })

      // Calculate team totals
      let completed = 0
      const total = profiles.length * totalTools

      const memberProgress: MemberProgress[] = profiles.map((p) => {
        const userTools = responseMap.get(p.id)
        const count = userTools ? userTools.size : 0
        completed += count
        return {
          name: p.display_name ?? p.email ?? 'Unknown',
          count,
          isMe: p.id === userId,
        }
      })

      memberProgress.sort((a, b) => b.count - a.count)

      setTeamCompleted(completed)
      setTeamTotal(total)
      setTeamPct(total > 0 ? Math.round((completed / total) * 100) : 0)
      setMembers(memberProgress)
      setLoading(false)
    }

    loadTeamProgress()
  }, [profile, userId, totalTools])

  if (!profile?.team_id) return null

  if (loading) {
    return (
      <div className={styles.section} data-testid="my-team-panel-loading">
        <div className={styles.secLabel}>
          <span className={styles.dot} />
          My Team
        </div>
        <div className={styles.loadingText}>Loading team data...</div>
      </div>
    )
  }

  return (
    <div className={styles.section} data-testid="my-team-panel">
      <div className={styles.secLabel}>
        <span className={styles.dot} />
        My Team
      </div>

      {/* Team Progress */}
      <div className={styles.card} data-testid="team-progress-card">
        <div className={styles.hdrBlue}>Team Progress</div>
        <div className={styles.barRow}>
          <div className={styles.barTrack}>
            <div className={styles.barFillBlue} style={{ width: `${teamPct}%` }} />
          </div>
          <span className={styles.pct}>{teamPct}%</span>
        </div>
        <div className={styles.caption}>
          {teamCompleted} of {teamTotal} total tool completions across the team
        </div>
      </div>

      {/* Individual Progress */}
      <div className={styles.card} data-testid="individual-progress-card">
        <div className={styles.hdrDark}>Individual Progress</div>
        {members.map((m) => {
          const pct = Math.round((m.count / totalTools) * 100)
          const initials = m.name
            .split(' ')
            .map((w) => w.charAt(0))
            .join('')
            .substring(0, 2)
            .toUpperCase()

          return (
            <div className={styles.memberRow} key={m.name} data-testid={`member-progress-${m.name}`}>
              <div className={styles.avatar}>{initials}</div>
              <div className={styles.memberInfo}>
                <div className={styles.memberName}>
                  {m.name}
                  {m.isMe && <span className={styles.youTag}>(you)</span>}
                </div>
                <div className={styles.memberBarRow}>
                  <div className={styles.barTrackSmall}>
                    <div
                      className={m.isMe ? styles.barFillOrange : styles.barFillBlue}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span className={styles.memberCount}>{m.count}/{totalTools}</span>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
